import {
  Button,
  Card,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@repo/ui";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const heroBg =
  "url(https://images.pexels.com/photos/957040/night-photograph-starry-sky-night-sky-star-957040.jpeg)";

type NewsCard = {
  id: number;
  slug: string;
  title: string;
  description: string;
  created_at: string;
  cover_image: string | null;
};

export function Home() {
  const [latestNews, setLatestNews] = useState<NewsCard[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/news");
        const data = (await res.json()) as { status: string; data: NewsCard[] };
        setLatestNews((data.data || []).slice(0, 3));
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <div className="space-y-14">
      <section className="relative overflow-hidden rounded-2xl border border-border/60">
        <div
          className="absolute inset-0 bg-center bg-cover opacity-40"
          style={{ backgroundImage: heroBg }}
        />
        <div className="relative px-6 py-16 md:px-12 md:py-24">
          <h1 className="font-display text-3xl md:text-5xl leading-tight max-w-3xl">
            อ่านดวงวันนี้แบบแม่นยำกับ Lucky News
          </h1>
          <p className="mt-4 text-foreground/80 max-w-2xl">
            สำรวจดวงรายวัน 12 ราศี เช็กความเข้ากันได้จากวันเกิด และอ่านบทความเสริมดวง
            เพื่อเริ่มต้นวันของคุณอย่างมั่นใจ
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/daily">อ่านดวงวันนี้</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/compatibility">เช็กความเข้ากันได้</Link>
            </Button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-4">เรื่องเด่นวันนี้</h2>
        <Carousel className="w-full">
          <CarouselContent>
            {slides.map((s) => (
              <CarouselItem key={s.href} className="md:basis-1/2 lg:basis-1/3">
                <Link
                  to={s.href}
                  className="block overflow-hidden rounded-xl border border-border/60"
                >
                  <div
                    className="aspect-[16/9] bg-cover bg-center"
                    style={{ backgroundImage: `url(${s.img})` }}
                  />
                  <div className="p-4 bg-card/70">
                    <div className="text-xs text-foreground/60">{s.tag}</div>
                    <div className="font-display mt-1">{s.title}</div>
                  </div>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-6">ข่าวดวงล่าสุด</h2>
        {latestNews.length === 0 ? (
          <p className="text-sm text-foreground/60">ยังไม่มีข่าวล่าสุด</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {latestNews.map((n) => {
              const plain = n.description.replace(/<[^>]+>/g, "");
              return (
                <Card
                  key={n.id}
                  className="overflow-hidden bg-card/60 hover:bg-card transition-colors border border-border/60"
                >
                  {n.cover_image && (
                    <div className="aspect-[16/9] w-full overflow-hidden">
                      <img
                        src={n.cover_image}
                        alt={n.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="text-xs text-foreground/60">
                      {new Date(n.created_at).toLocaleDateString("th-TH")}
                    </div>
                    <Link
                      to={`/news/${n.slug}`}
                      className="block mt-1 font-display text-lg hover:text-primary"
                    >
                      {n.title}
                    </Link>
                    <p className="text-sm text-foreground/70 mt-1 line-clamp-3">{plain}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-2xl mb-6">12 ราศี (ตะวันตก)</h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {zodiacs.map((z) => (
            <Link
              key={z.key}
              to={`/daily?sign=${z.key}`}
              className="rounded-lg border border-border/60 px-3 py-2 text-center hover:border-primary/60 hover:text-primary"
            >
              {z.name}
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-6">เลขศาสตร์/วันเกิด</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-4 bg-card/60">
            <div className="font-display">เลขนำโชควันนี้</div>
            <p className="text-sm text-foreground/70 mt-1">สุ่มเลขนำโชค 3 ตัวสำหรับคุณวันนี้</p>
            <div className="mt-3 text-2xl tracking-widest">{luckyNumbers().join(" ")}</div>
          </Card>
          <Card className="p-4 bg-card/60">
            <div className="font-display">สีมงคล</div>
            <p className="text-sm text-foreground/70 mt-1">โทนสีช่วยเสริมพลังงานบวกในวันนี้</p>
            <div className="mt-3 inline-flex gap-2 items-center">
              {luckyColors().map((c) => (
                <span
                  key={c.hex}
                  className="size-6 rounded"
                  style={{ backgroundColor: c.hex }}
                  title={c.name}
                />
              ))}
            </div>
          </Card>
          <Card className="p-4 bg-card/60">
            <div className="font-display">เริ่มต้นที่นี่</div>
            <p className="text-sm text-foreground/70 mt-1">
              อยากรู้ความเข้ากันได้? ลองเครื่องมือของเรา
            </p>
            <Button asChild className="mt-3">
              <Link to="/compatibility">เช็กเลย</Link>
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
}

const zodiacs = [
  { key: "aries", name: "เมษ" },
  { key: "taurus", name: "พฤษภ" },
  { key: "gemini", name: "เมถุน" },
  { key: "cancer", name: "กรกฎ" },
  { key: "leo", name: "สิงห์" },
  { key: "virgo", name: "กันย์" },
  { key: "libra", name: "ตุล" },
  { key: "scorpio", name: "พิจิก" },
  { key: "sagittarius", name: "ธนู" },
  { key: "capricorn", name: "มังกร" },
  { key: "aquarius", name: "กุมภ์" },
  { key: "pisces", name: "มีน" },
];

function luckyNumbers() {
  const seed = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const rand = (i: number) => (parseInt(seed.slice(i, i + 2)) * (i + 3)) % 10;
  return [rand(0), rand(2), rand(4)];
}

function luckyColors() {
  const colors = [
    { name: "ม่วงอมฟ้า", hex: "#7C83FD" },
    { name: "กรมท่า", hex: "#1F2A44" },
    { name: "ชมพูอมม่วง", hex: "#B388FF" },
    { name: "ฟ้าน้ำทะเลเข้ม", hex: "#0E7490" },
  ];
  const day = new Date().getDay();
  return [colors[day % colors.length], colors[(day + 1) % colors.length]];
}

const slides = [
  {
    href: "/daily",
    img: "https://images.pexels.com/photos/6039245/pexels-photo-6039245.jpeg",
    title: "ดวงวันนี้ 12 ราศี",
    tag: "ดวงรายวัน",
  },
  {
    href: "/compatibility",
    img: "https://images.pexels.com/photos/1252871/pexels-photo-1252871.jpeg",
    title: "เช็กความเข้ากันได้จากวันเกิด",
    tag: "ความรัก",
  },
  {
    href: "/news",
    img: "https://images.pexels.com/photos/1624360/pexels-photo-1624360.jpeg",
    title: "ข่าวดวงและเคล็ดลับเสริมดวง",
    tag: "ข่าว & บทความ",
  },
];

export default Home;
